import React, { ChangeEvent } from 'react'
import { NameDto } from '@/api/Common/Types'
import { CurrencyType } from '@/api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from '@/api/Wallet/Types/WalletCurrencyDto'
import { Button } from '@/components/ui/button'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export enum InterestRateEligibilityCriteriaType {
  ActiveTradingDays = 1,
  OpenPositions = 2,
  Deposit = 3,
  MinimumAmountCurrency = 4,
  SwapFree = 5,
}
export interface AddInterestRateEligibilityFormValues {
  interestRateEligibilityCriteriaTypeId: number
  value: string | boolean
}

const AddInterestRateEligibilityFormUI: React.FC<
  FormikProps<AddInterestRateEligibilityFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, values } = props
  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }
  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Eligibility Criteria</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const valueId = parseInt(e.target.value, 10)
                  const type = props.types.find((t) => t.id === valueId)
                  if (type?.id === InterestRateEligibilityCriteriaType.SwapFree) {
                    props.setValues({
                      interestRateEligibilityCriteriaTypeId: type?.id ?? null,
                      value: true,
                    })
                  } else {
                    props.setFieldValue('interestRateEligibilityCriteriaTypeId', type?.id ?? null)
                  }
                }}
                component='select'
                name='interestRateEligibilityCriteriaTypeId'
              >
                <option value='' disabled>
                  None
                </option>
                {(props.types || []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.interestRateEligibilityCriteriaTypeId && (
            <FormBaseErrorMessage>
              {errors.interestRateEligibilityCriteriaTypeId}
            </FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Value</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              {values.interestRateEligibilityCriteriaTypeId ===
                InterestRateEligibilityCriteriaType.ActiveTradingDays && (
                <Field type='number' name='value' />
              )}
              {values.interestRateEligibilityCriteriaTypeId ===
                InterestRateEligibilityCriteriaType.OpenPositions && (
                <Field type='number' name='value' />
              )}
              {values.interestRateEligibilityCriteriaTypeId ===
                InterestRateEligibilityCriteriaType.Deposit && <Field type='number' name='value' />}
              {values.interestRateEligibilityCriteriaTypeId ===
                InterestRateEligibilityCriteriaType.MinimumAmountCurrency && (
                <Field
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    const currTypeId = e.target.value as CurrencyType
                    const currency = props.currencies.find((t) => t.id === currTypeId)
                    props.setFieldValue('value', currency?.id ?? null)
                  }}
                  component='select'
                  name='value'
                >
                  <option value='' disabled>
                    None
                  </option>
                  {(props.currencies || []).map((curr) => (
                    <option key={curr.id} value={curr.id}>
                      {curr.id}
                    </option>
                  ))}
                </Field>
              )}
              {values.interestRateEligibilityCriteriaTypeId ===
                InterestRateEligibilityCriteriaType.SwapFree && (
                <Field type='checkbox' name='value' checked={Boolean(values.value)} />
              )}
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.value && <FormBaseErrorMessage>{errors.value}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' onClick={handleSubmitForm} type='button'>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  currencies: WalletCurrencyDto[]
  permissions: PermissionDto[]
  types: NameDto[]

  onSubmit(values: FormSubmitValues<AddInterestRateEligibilityFormValues>): Promise<void>
}

export const AddInterestRateEligibilityForm = withFormik<
  OuterProps,
  AddInterestRateEligibilityFormValues
>({
  mapPropsToValues: ({ types }) => {
    return {
      interestRateEligibilityCriteriaTypeId: types.length ? types[0].id : 0,
      value: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddInterestRateEligibilityFormValues> = {}
    if (!values.interestRateEligibilityCriteriaTypeId) {
      errors.interestRateEligibilityCriteriaTypeId = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaTypeId ===
        InterestRateEligibilityCriteriaType.ActiveTradingDays &&
      !values.value
    ) {
      errors.value = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaTypeId ===
        InterestRateEligibilityCriteriaType.OpenPositions &&
      !values.value
    ) {
      errors.value = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaTypeId ===
        InterestRateEligibilityCriteriaType.Deposit &&
      !values.value
    ) {
      errors.value = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaTypeId ===
        InterestRateEligibilityCriteriaType.MinimumAmountCurrency &&
      !values.value
    ) {
      errors.value = 'required'
    }
    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
})(AddInterestRateEligibilityFormUI)
